import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export function useMetas() {
  const [metas, setMetas]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMetas()
  }, [])

  async function fetchMetas() {
    const { data, error } = await supabase
      .from('metas')
      .select('*')
      .order('created_at', { ascending: false })

    if (!error) setMetas(data)
    setLoading(false)
  }

  async function crearMeta(meta) {
    const { data: { user } } = await supabase.auth.getUser()
    const payload = {
      tipo: 'meta',
      status: 'en_progreso',
      fase: 'ahorrando',
      ...meta,
      user_id: user.id,
      acumulado: 0,
    }
    const { data, error } = await supabase
      .from('metas')
      .insert([payload])
      .select()
      .single()
    if (!error) setMetas(prev => [data, ...prev])
    return { data, error }
  }

  async function aportarAMeta(metaId, monto) {
    const { data: current, error: fetchErr } = await supabase
      .from('metas').select('acumulado').eq('id', metaId).single()
    if (fetchErr) return { error: fetchErr }

    const nuevoAcumulado = (current.acumulado || 0) + monto
    const { error } = await supabase
      .from('metas').update({ acumulado: nuevoAcumulado }).eq('id', metaId)

    if (!error) setMetas(prev => prev.map(m => m.id === metaId ? { ...m, acumulado: nuevoAcumulado } : m))
    return { error }
  }

  async function editarMeta(metaId, cambios) {
    const { data, error } = await supabase
      .from('metas')
      .update(cambios)
      .eq('id', metaId)
      .select()
      .single()

    if (!error) setMetas(prev => prev.map(m => m.id === metaId ? data : m))
    return { data, error }
  }

  async function editarStatus(metaId, status) {
    return editarMeta(metaId, { status })
  }

  async function editarFase(metaId, fase) {
    return editarMeta(metaId, { fase })
  }

  async function gastarDeMeta(metaId, monto) {
    const { data: current, error: fetchErr } = await supabase
      .from('metas').select('acumulado, gastado_meta').eq('id', metaId).single()
    if (fetchErr) return { error: fetchErr, disponible: null }
    const nuevoGastado = (current.gastado_meta || 0) + monto
    const disponible   = Math.max(0, (current.acumulado || 0) - nuevoGastado)
    const { error } = await supabase
      .from('metas').update({ gastado_meta: nuevoGastado }).eq('id', metaId)
    if (!error) setMetas(prev => prev.map(m => m.id === metaId ? { ...m, gastado_meta: nuevoGastado } : m))
    return { error, disponible }
  }

  async function eliminarMeta(metaId) {
    const { error } = await supabase.from('metas').delete().eq('id', metaId)
    if (!error) setMetas(prev => prev.filter(m => m.id !== metaId))
    return { error }
  }

  // meta_categorias — loaded on demand, not in global state

  async function fetchCategorias(metaId) {
    const { data, error } = await supabase
      .from('meta_categorias')
      .select('*')
      .eq('meta_id', metaId)
      .order('orden', { ascending: true })
    return { data: data || [], error }
  }

  async function crearCategoria(metaId, categoria) {
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('meta_categorias')
      .insert([{ ...categoria, meta_id: metaId, user_id: user.id }])
      .select()
      .single()
    return { data, error }
  }

  async function editarCategoria(categoriaId, cambios) {
    const { data, error } = await supabase
      .from('meta_categorias')
      .update(cambios)
      .eq('id', categoriaId)
      .select()
      .single()
    return { data, error }
  }

  async function eliminarCategoria(categoriaId) {
    const { error } = await supabase.from('meta_categorias').delete().eq('id', categoriaId)
    return { error }
  }

  async function gastarEnCategoria(categoriaId, monto) {
    const { data: current, error: fetchErr } = await supabase
      .from('meta_categorias').select('gastado').eq('id', categoriaId).single()
    if (fetchErr) return { error: fetchErr }
    const nuevoGastado = (current.gastado || 0) + monto
    const { error } = await supabase
      .from('meta_categorias').update({ gastado: nuevoGastado }).eq('id', categoriaId)
    return { error }
  }

  return {
    metas, loading,
    crearMeta, aportarAMeta, editarMeta, eliminarMeta, gastarDeMeta,
    editarStatus, editarFase,
    fetchCategorias, crearCategoria, editarCategoria, eliminarCategoria, gastarEnCategoria,
    refetch: fetchMetas,
  }
}
